import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { apiClient } from '../services/api'
import { useAuthStore } from '../services/authStore'
import queueService from '../services/queueService'
import dashboardSocketService from '../services/dashboardSocketService'
import { useDashboardStore } from '../services/dashboardStore'
import { useAlertStore } from '../services/alertStore'
import usePolling from '../hooks/usePolling'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import AlertBanner from '../components/AlertBanner'
import Toast from '../components/Toast'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import { useTranslation } from '../contexts/TranslationContext'
import { ui } from '../styles/ui'
import { formatCurrency } from '../utils/currency'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'

const HEALTH_BADGE_CLASS = {
  active: `${ui.badge} ${ui.badgeSuccess}`,
  idle: `${ui.badge} ${ui.badgeNeutral}`,
  failed: `${ui.badge} ${ui.badgeError}`,
  unavailable: `${ui.badge} ${ui.badgeNeutral}`,
}

const PIPELINE_BADGE_CLASS = {
  OK: `${ui.badge} ${ui.badgeSuccess}`,
  DELAYED: `${ui.badge} ${ui.badgeWarning}`,
  FAILING: `${ui.badge} ${ui.badgeError}`,
}

const JOB_BADGE_CLASS = {
  completed: `${ui.badge} ${ui.badgeSuccess}`,
  failed: `${ui.badge} ${ui.badgeError}`,
  pending: `${ui.badge} ${ui.badgeNeutral}`,
  processing: `${ui.badge} ${ui.badgeWarning}`,
  unknown: `${ui.badge} ${ui.badgeNeutral}`,
}

const QUEUE_LABELS = {
  receiptAnalysis: 'FAST OCR',
  productAnalysis: 'SLOW OCR',
  qrcodeProductExtraction: 'QR',
  speech: 'SPEECH',
}

const STUCK_JOB_THRESHOLD_MS = 30 * 60 * 1000

function parseTimestamp(value) {
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime()
}

function toQueueMap(queues) {
  return (Array.isArray(queues) ? queues : []).reduce((acc, queue) => {
    acc[queue?.key] = queue
    return acc
  }, {})
}

function resolveJobStatus(job) {
  const statuses = [
    job?.job_status,
    job?.jobStatus,
    job?.status,
    job?.ocr_status,
    job?.ocrStatus,
    job?.product_analysis_status,
    job?.productAnalysisStatus,
    job?.product_translation_status,
    job?.productTranslationStatus,
    job?.qrcode_extraction_status,
    job?.qrcodeExtractionStatus,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())

  if (statuses.some((status) => status === 'failed')) return 'failed'
  if (statuses.some((status) => status === 'processing')) return 'processing'
  if (statuses.some((status) => status === 'pending')) return 'pending'
  if (statuses.some((status) => status === 'completed' || status === 'done')) return 'completed'
  return 'unknown'
}

function resolveJobType(job) {
  const source = String(job?.source || '').toLowerCase()
  if (source === 'qrcode') return 'QR'
  if (source === 'speech') return 'Speech'
  return 'OCR'
}

function resolveHealthStatus(activeJobs, failedJobs, enabled) {
  if (!enabled) return 'idle'
  if (activeJobs == null || failedJobs == null) return 'unavailable'
  if (failedJobs > 0) return 'failed'
  if (activeJobs > 0) return 'active'
  return 'idle'
}

function stageCount(queue) {
  if (typeof queue.pending !== 'number' || typeof queue.processing !== 'number') return null
  return queue.pending + queue.processing
}

function resolvePipelineStatus(pending, processing, failed, enabled) {
  if (!enabled) return 'DELAYED'
  if (pending == null || processing == null || failed == null) return 'UNAVAILABLE'
  if (failed > 0) return 'FAILING'
  if (pending > 0 || processing > 0) return 'DELAYED'
  return 'OK'
}

function normalizeMetricsPayload(metricsPayload, queueRows, receiptSummary) {
  const payload = metricsPayload && typeof metricsPayload === 'object' ? metricsPayload : {}
  return {
    queues: Array.isArray(payload.queues) ? payload.queues : queueRows,
    summary: payload.summary || {},
    receiptSummary: receiptSummary || {
      totalCount: 0,
      monthlyCount: 0,
      average: 0,
    },
  }
}

export default function Dashboard() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { t } = useTranslation()

  const {
    queues,
    jobs,
    metrics,
    queueProcessing,
    recentChanges,
    loading,
    error,
    updatedAt,
    setLoading,
    setError,
    applySnapshot,
  } = useDashboardStore()

  const {
    alerts,
    ingestRemoteAlerts,
    addLocalAlert,
    removeAlert,
  } = useAlertStore()
  const [retryDialogOpen, setRetryDialogOpen] = useState(false)
  const [clearDialogOpen, setClearDialogOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [socketConnected, setSocketConnected] = useState(false)

  // Poll all dashboard sources as one batch to keep sections synchronized.
  const fetchDashboardSnapshot = useCallback(async () => {
    try {
      if (!updatedAt) setLoading(true)
      setError('')

      const [summaryRes, queuesRes, jobsRes, workerRes, metricsRes] = await Promise.all([
        apiClient.get('/dashboard/summary'),
        queueService.adminQueues(),
        queueService.adminJobs({ status: 'all', page: 1, per_page: 25 }),
        queueService.status(),
        queueService.adminMetrics(),
      ])

      const summaryData = summaryRes?.data?.data || {}
      const receiptSummary = {
        totalCount: Number(summaryData.total_count ?? summaryData.receipt_count ?? 0) || 0,
        monthlyCount: Number(summaryData.monthly_count ?? 0) || 0,
        average: Number(summaryData.average_receipt ?? 0) || 0,
      }

      const queueRows = queuesRes?.data?.data || []
      const jobRows = jobsRes?.data?.data || []
      const queueProcessingFlags = workerRes?.queue_processing && typeof workerRes.queue_processing === 'object'
        ? workerRes.queue_processing
        : {}

      applySnapshot({
        queues: queueRows,
        jobs: jobRows,
        queueProcessing: queueProcessingFlags,
        metrics: normalizeMetricsPayload(metricsRes, queueRows, receiptSummary),
      })
    } catch (fetchError) {
      console.error('Failed to poll dashboard data', fetchError)
      setError(t('Unable to load dashboard control data.', 'Unable to load dashboard control data.'))
      setLoading(false)
    }
  }, [updatedAt, setLoading, setError, applySnapshot, t])

  const fetchAlerts = useCallback(async () => {
    try {
      const rows = await queueService.adminAlerts({ limit: 100 })
      ingestRemoteAlerts(rows)
    } catch {
      // Alerts are secondary; keep dashboard non-blocking if alerts endpoint fails.
    }
  }, [ingestRemoteAlerts])

  usePolling(fetchDashboardSnapshot, { intervalMs: 8000, immediate: true, enabled: !socketConnected })
  usePolling(fetchAlerts, { intervalMs: 5000, immediate: true, enabled: !socketConnected })

  useEffect(() => {
    dashboardSocketService.setFallbackHandler(() => {
      setSocketConnected(false)
    })
    const unsubOpen = dashboardSocketService.subscribe('socket:open', () => {
      setSocketConnected(true)
    })
    const unsubError = dashboardSocketService.subscribe('socket:error', () => {
      setSocketConnected(false)
    })
    const unsubSnapshot = dashboardSocketService.subscribe('dashboard:snapshot', async () => {
      await fetchDashboardSnapshot()
      await fetchAlerts()
    })
    const unsubAlertNew = dashboardSocketService.subscribe('alert:new', fetchAlerts)
    const unsubAlertUpdated = dashboardSocketService.subscribe('alert:updated', fetchAlerts)
    const unsubAlertResolved = dashboardSocketService.subscribe('alert:resolved', fetchAlerts)

    dashboardSocketService.connect()
    return () => {
      unsubOpen()
      unsubError()
      unsubSnapshot()
      unsubAlertNew()
      unsubAlertUpdated()
      unsubAlertResolved()
      dashboardSocketService.disconnect()
    }
  }, [fetchDashboardSnapshot, fetchAlerts])

  const queueStats = useMemo(() => {
    if (Array.isArray(metrics?.queues) && metrics.queues.length > 0) return metrics.queues
    return queues
  }, [metrics, queues])

  const queueByKey = useMemo(() => toQueueMap(queueStats), [queueStats])

  const sortedJobs = useMemo(() => {
    return [...(Array.isArray(jobs) ? jobs : [])].sort((a, b) => parseTimestamp(b?.updated_at ?? b?.updatedAt) - parseTimestamp(a?.updated_at ?? a?.updatedAt))
  }, [jobs])

  const recentJobs = useMemo(() => sortedJobs.slice(0, 10), [sortedJobs])

  const failedJobs = useMemo(() => sortedJobs.filter((job) => resolveJobStatus(job) === 'failed'), [sortedJobs])

  const stuckJobs = useMemo(() => {
    const now = Date.now()
    return sortedJobs.filter((job) => {
      const status = resolveJobStatus(job)
      if (!['pending', 'processing'].includes(status)) return false
      const updatedAt = job?.updated_at ?? job?.updatedAt
      return now - parseTimestamp(updatedAt) >= STUCK_JOB_THRESHOLD_MS
    })
  }, [sortedJobs])

  const speechJobs = useMemo(
    () => sortedJobs.filter((job) => String(job?.source || '').toLowerCase() === 'speech'),
    [sortedJobs],
  )

  const systemHealthCards = useMemo(() => {
    const fast = queueByKey.receiptAnalysis || {}
    const slow = queueByKey.productAnalysis || {}
    const qr = queueByKey.qrcodeProductExtraction || {}
    const productTranslation = queueByKey.productTranslation || {}
    const speech = queueByKey.speech || {}

    const qrEnabled = queueProcessing.qrcode ?? true
    const speechEnabled = queueProcessing.speech ?? true

    const cards = [
      { label: 'OCR Fast Queue', activeJobs: stageCount(fast), failedJobs: fast.failed, enabled: queueProcessing.fast ?? true },
      { label: 'OCR Slow Queue', activeJobs: stageCount(slow), failedJobs: slow.failed, enabled: queueProcessing.slow ?? true },
      { label: 'QR Queue', activeJobs: stageCount(qr), failedJobs: qr.failed, enabled: qrEnabled },
      { label: 'Speech Queue', activeJobs: stageCount(speech), failedJobs: speech.failed, enabled: speechEnabled },
      { label: 'Product Categorization / Translation Queue', activeJobs: stageCount(productTranslation), failedJobs: productTranslation.failed, enabled: queueProcessing.translate ?? true },
    ]

    return cards.map((card) => ({
      ...card,
      status: resolveHealthStatus(card.activeJobs, card.failedJobs, card.enabled),
    }))
  }, [queueByKey, queueProcessing])

  const pipelineRows = useMemo(() => {
    const rows = [
      { key: 'receiptAnalysis', label: 'FAST OCR', enabled: queueProcessing.fast ?? true },
      { key: 'productAnalysis', label: 'SLOW OCR', enabled: queueProcessing.slow ?? true },
      { key: 'qrcodeProductExtraction', label: 'QR Parsing', enabled: queueProcessing.qrcode ?? true },
      { key: 'productTranslation', label: 'Product Categorization / Translation', enabled: queueProcessing.translate ?? true },
    ].map((pipeline) => {
      const stats = queueByKey[pipeline.key] || {}
      const status = resolvePipelineStatus(
        stats.pending,
        stats.processing,
        stats.failed,
        pipeline.enabled,
      )
      const latestJob = sortedJobs
        .filter((job) => job?.queue_key === pipeline.key || job?.queueKey === pipeline.key)
        .sort((a, b) => parseTimestamp(b?.updated_at ?? b?.updatedAt) - parseTimestamp(a?.updated_at ?? a?.updatedAt))[0]
      return {
        label: pipeline.label,
        status,
        pending: stats.pending ?? null,
        processing: stats.processing ?? null,
        failed: stats.failed ?? null,
        lastRun: latestJob?.updated_at ?? latestJob?.updatedAt ?? null,
      }
    })

    const latestSpeechJob = speechJobs
      .slice()
      .sort((a, b) => parseTimestamp(b?.updated_at ?? b?.updatedAt) - parseTimestamp(a?.updated_at ?? a?.updatedAt))[0]
    rows.push({
      label: 'Speech Parsing',
      status: resolvePipelineStatus(
        queueByKey.speech?.pending ?? null,
        queueByKey.speech?.processing ?? null,
        queueByKey.speech?.failed ?? null,
        queueProcessing.speech ?? true,
      ),
      pending: queueByKey.speech?.pending ?? null,
      processing: queueByKey.speech?.processing ?? null,
      failed: queueByKey.speech?.failed ?? null,
      lastRun: latestSpeechJob?.updated_at ?? latestSpeechJob?.updatedAt ?? null,
    })

    return rows
  }, [queueByKey, queueProcessing, sortedJobs])

  const bannerAlerts = useMemo(
    () => alerts.filter((alert) => alert?.severity === 'error'),
    [alerts],
  )

  const toastAlerts = useMemo(
    () => alerts.filter((alert) => alert?.severity === 'info'),
    [alerts],
  )
  const warningAlerts = useMemo(
    () => alerts.filter((alert) => alert?.severity === 'warning').slice(0, 10),
    [alerts],
  )

  const lastErrors = useMemo(() => {
    const rows = bannerAlerts.slice(0, 10)
    return rows.map((alert) => ({
      id: alert.id,
      type: alert.type || 'error',
      message: alert.message,
      timestamp: alert.timestamp,
      receiptId: alert?.metadata?.receipt_id || null,
    }))
  }, [bannerAlerts])

  const handleDismissAlert = useCallback((alertId) => {
    removeAlert(alertId)
  }, [removeAlert])

  const handleAcknowledgeAlert = useCallback(async (alertId) => {
    const target = alerts.find((alert) => alert.id === alertId)
    if (!target) return
    try {
      if (!target.local) {
        await queueService.ackAlert(alertId)
      }
    } catch {
      // Keep UI responsive even if ack request fails.
    } finally {
      removeAlert(alertId)
    }
  }, [alerts, removeAlert])

  const handleRetryFailedJobs = useCallback(async () => {
    if (!failedJobs.length) return
    setActionLoading(true)
    try {
      await Promise.allSettled(failedJobs.slice(0, 10).map((job) => queueService.retryJob(job.id)))
      addLocalAlert({
        type: 'retry_success',
        severity: 'info',
        message: `${t('Retried', 'Retried')} ${Math.min(failedJobs.length, 10)} ${t('failed jobs', 'failed jobs')}`,
        persistent: false,
      })
      await fetchDashboardSnapshot()
    } finally {
      setActionLoading(false)
      setRetryDialogOpen(false)
    }
  }, [failedJobs, addLocalAlert, fetchDashboardSnapshot, t])

  const handleClearStuckJobs = useCallback(async () => {
    if (!stuckJobs.length) return
    setActionLoading(true)
    try {
      await Promise.allSettled(stuckJobs.slice(0, 10).map((job) => queueService.removeJob(job.id)))
      addLocalAlert({
        type: 'stuck_jobs_cleared',
        severity: 'info',
        message: `${t('Cleared', 'Cleared')} ${Math.min(stuckJobs.length, 10)} ${t('stuck jobs', 'stuck jobs')}`,
        persistent: false,
      })
      await fetchDashboardSnapshot()
    } finally {
      setActionLoading(false)
      setClearDialogOpen(false)
    }
  }, [stuckJobs, addLocalAlert, fetchDashboardSnapshot, t])

  const handleAcknowledgeWarnings = useCallback(async () => {
    await Promise.allSettled(
      warningAlerts.map((alert) => handleAcknowledgeAlert(alert.id)),
    )
  }, [warningAlerts, handleAcknowledgeAlert])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className={ui.spinnerLg} />
      </div>
    )
  }

  return (
    <PageShell>
      <AlertBanner
        alerts={bannerAlerts}
        onDismiss={handleDismissAlert}
        onAcknowledge={handleAcknowledgeAlert}
      />

      {toastAlerts.map((alert, index) => (
        <Toast
          key={alert.id}
          alert={alert}
          index={index}
          onDismiss={handleDismissAlert}
          onAcknowledge={handleAcknowledgeAlert}
        />
      ))}

      <PageHeader
        title={t('System Control Panel', 'System Control Panel')}
        subtitle={t('Live queue health, pipeline signals, and debugging actions.', 'Live queue health, pipeline signals, and debugging actions.')}
        actions={<Button variant="secondary" onClick={fetchDashboardSnapshot}>{t('Refresh', 'Refresh')}</Button>}
      />
      {updatedAt && (
        <p className="text-xs text-muted-foreground">
          {t('Updated at', 'Updated at')}: {formatDateTime(updatedAt)}
        </p>
      )}

      {error && <div className={ui.alertInfo}>{error}</div>}

      <section className={ui.section}>
        <h2 className="text-lg font-semibold text-foreground">{t('System Health', 'System Health')}</h2>
        <p className="text-sm text-muted-foreground">{t('These are PostgreSQL receipt-stage statuses, not physical BullMQ job counts.', 'These are PostgreSQL receipt-stage statuses, not physical BullMQ job counts.')}</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-6">
          {systemHealthCards.map((card) => (
            <Card key={card.label}>
              <div className={ui.cardBody}>
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-foreground">{card.label}</h3>
                  <span className={HEALTH_BADGE_CLASS[card.status]}>{card.status}</span>
                </div>
                <div className="mt-4 space-y-1 text-sm text-muted-foreground">
                  <div>{t('Pending + processing stages', 'Pending + processing stages')}: <span className="font-semibold text-foreground">{card.activeJobs ?? formatEmptyValue(null)}</span></div>
                  <div>{t('Failed stages', 'Failed stages')}: <span className="font-semibold text-foreground">{card.failedJobs ?? formatEmptyValue(null)}</span></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className={ui.section}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">{t('Active Alerts', 'Active Alerts')}</h2>
          <Button variant="secondary" onClick={handleAcknowledgeWarnings} disabled={warningAlerts.length === 0}>
            {t('Acknowledge Warnings', 'Acknowledge Warnings')}
          </Button>
        </div>
        <Card>
          <div className={`${ui.cardBody} space-y-2`}>
            {warningAlerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('No warning alerts.', 'No warning alerts.')}</p>
            ) : warningAlerts.map((alert) => (
              <div key={alert.id} className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">{alert.type || 'warning'}</p>
                  <p className="text-sm text-amber-900">{alert.message}</p>
                </div>
                <Button variant="secondary" onClick={() => handleAcknowledgeAlert(alert.id)}>
                  {t('Acknowledge', 'Acknowledge')}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </section>

      <section className={ui.section}>
        <h2 className="text-lg font-semibold text-foreground">{t('Receipt workflow stage status', 'Receipt workflow stage status')}</h2>
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className={ui.table}>
              <thead className={ui.tableHead}>
                <tr>
                  <th className={ui.tableHeadCell}>{t('Pipeline', 'Pipeline')}</th>
                  <th className={ui.tableHeadCell}>{t('Status', 'Status')}</th>
                  <th className={ui.tableHeadCell}>{t('Pending stage', 'Pending stage')}</th>
                  <th className={ui.tableHeadCell}>{t('Processing stage', 'Processing stage')}</th>
                  <th className={ui.tableHeadCell}>{t('Failed stage', 'Failed stage')}</th>
                  <th className={ui.tableHeadCell}>{t('Last run', 'Last run')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider bg-surface text-sm text-foreground">
                {pipelineRows.map((row) => (
                  <tr key={row.label}>
                    <td className={ui.tableCell}>
                      <div className="font-medium text-foreground">{row.label}</div>
                    </td>
                    <td className={ui.tableCell}>
                      <span className={PIPELINE_BADGE_CLASS[row.status]}>{row.status}</span>
                    </td>
                    <td className={ui.tableCellText}>{row.pending ?? formatEmptyValue(null)}</td>
                    <td className={ui.tableCellText}>{row.processing ?? formatEmptyValue(null)}</td>
                    <td className={ui.tableCellText}>{row.failed ?? formatEmptyValue(null)}</td>
                    <td className={ui.tableCellText}>{row.lastRun ? formatDateTime(row.lastRun) : formatEmptyValue(null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      <section className={ui.section}>
        <h2 className="text-lg font-semibold text-foreground">{t('Action Center', 'Action Center')}</h2>
        <Card>
          <div className={`${ui.cardBody} flex flex-wrap gap-3`}>
            <Button onClick={() => setRetryDialogOpen(true)} disabled={failedJobs.length === 0}>
              {t('Retry Failed Jobs', 'Retry Failed Jobs')} ({failedJobs.length})
            </Button>
            <button type="button" onClick={() => setClearDialogOpen(true)} disabled={stuckJobs.length === 0} className={ui.buttonGhost}>
              {t('Clear Stuck Jobs', 'Clear Stuck Jobs')} ({stuckJobs.length})
            </button>
            <button type="button" onClick={() => navigate('/admin/queue-workers')} className={ui.buttonSmPrimary}>
              {t('Open Queue Debug', 'Open Queue Debug')}
            </button>
          </div>
        </Card>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className={ui.section}>
          <h2 className="text-lg font-semibold text-foreground">{t('Recent Jobs', 'Recent Jobs')}</h2>
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className={ui.table}>
                <thead className={ui.tableHead}>
                  <tr>
                    <th className={ui.tableHeadCell}>{t('Type', 'Type')}</th>
                    <th className={ui.tableHeadCell}>{t('Status', 'Status')}</th>
                    <th className={ui.tableHeadCell}>{t('Time', 'Time')}</th>
                    <th className={ui.tableHeadCell}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider bg-surface text-sm text-foreground">
                  {recentJobs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-6 text-center text-sm text-muted-foreground">
                        {t('No jobs available.', 'No jobs available.')}
                      </td>
                    </tr>
                  ) : (
                    recentJobs.map((job) => {
                      const status = resolveJobStatus(job)
                      const change = recentChanges[job.id]
                      const rowClass = change?.kind === 'failed'
                        ? 'bg-red-50'
                        : change?.kind === 'completed'
                          ? 'bg-emerald-50'
                          : ''

                      return (
                        <tr key={job.id} className={rowClass}>
                          <td className={ui.tableCell}>
                            <div className="font-medium text-foreground">{resolveJobType(job)}</div>
                            <div className="text-xs text-muted-foreground">{QUEUE_LABELS[job.queue_key] || job.queue_label || t('Queue', 'Queue')}</div>
                          </td>
                          <td className={ui.tableCell}>
                            <span className={JOB_BADGE_CLASS[status] || JOB_BADGE_CLASS.unknown}>{status}</span>
                          </td>
                          <td className={ui.tableCellText}>{job.updated_at ? formatDateTime(job.updated_at) : formatEmptyValue(null)}</td>
                          <td className={ui.tableCell}>
                            <Link to={`/admin/jobs/${job.id}`} className={ui.buttonSmPrimary}>
                              {t('View', 'View')}
                            </Link>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </section>

        <section className={ui.section}>
          <h2 className="text-lg font-semibold text-foreground">{t('Last Errors', 'Last Errors')}</h2>
          <Card>
            <div className={`${ui.cardBody} space-y-3`}>
              {lastErrors.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('No recent errors.', 'No recent errors.')}</p>
              ) : (
                lastErrors.map((item) => (
                  <div key={item.id} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-red-700">{item.type}</span>
                      <span className="text-xs text-red-600">{item.timestamp ? formatDateTime(item.timestamp) : formatEmptyValue(null)}</span>
                    </div>
                    <p className="mt-1 text-sm text-red-800">{item.message}</p>
                    {item.receiptId && (
                      <div className="mt-2">
                        <Link to={`/admin/jobs/${item.receiptId}`} className={ui.buttonSmDanger}>{t('Open job', 'Open job')}</Link>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </Card>
        </section>
      </div>

      <section className={ui.section}>
        <h2 className="text-base font-semibold text-foreground">{t('Data Summary', 'Data Summary')}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Card>
            <div className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('Total receipts', 'Total receipts')}</p>
              <p className="mt-1 text-xl font-semibold text-foreground">{metrics?.receiptSummary?.totalCount ?? 0}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('Monthly receipts', 'Monthly receipts')}</p>
              <p className="mt-1 text-xl font-semibold text-foreground">{metrics?.receiptSummary?.monthlyCount ?? 0}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('Average receipt', 'Average receipt')}</p>
              <p className="mt-1 text-xl font-semibold text-foreground">
                {formatCurrency(metrics?.receiptSummary?.average ?? 0, user?.default_currency || 'USD')}
              </p>
            </div>
          </Card>
        </div>
      </section>

      <ConfirmDialog
        open={retryDialogOpen}
        title={t('Retry failed jobs', 'Retry failed jobs')}
        message={t('Retry the latest failed jobs now?', 'Retry the latest failed jobs now?')}
        onConfirm={handleRetryFailedJobs}
        onCancel={() => setRetryDialogOpen(false)}
        loading={actionLoading}
        confirmText={t('Retry', 'Retry')}
      />

      <ConfirmDialog
        open={clearDialogOpen}
        title={t('Clear stuck jobs', 'Clear stuck jobs')}
        message={t('Remove current stuck jobs from active queues?', 'Remove current stuck jobs from active queues?')}
        onConfirm={handleClearStuckJobs}
        onCancel={() => setClearDialogOpen(false)}
        loading={actionLoading}
        confirmText={t('Clear', 'Clear')}
      />
    </PageShell>
  )
}

